import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Divider, 
  Grid, 
  TextField, 
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import rfidService, { Reader } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';

const KeyRevocation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [reader, setReader] = useState<Reader | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [revoking, setRevoking] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  
  useEffect(() => {
    if (id) {
      fetchReader(id);
    }
  }, [id]);

  const fetchReader = async (readerId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getReaderById(readerId);
      setReader(data);
      
      // Redirect if the reader doesn't have an active key
      if (!data.auth_key_created_at || data.is_revoked) {
        toast.error('This reader does not have an active authentication key');
        navigate(`/rfid/readers/${readerId}`);
      }
    } catch (error) {
      console.error('Error fetching reader:', error);
      toast.error('Failed to fetch reader details');
    } finally {
      setLoading(false);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleRevokeKey = async () => {
    if (!id) return;
    
    try {
      setRevoking(true);
      await rfidService.revokeReaderKey(id, reason || undefined);
      toast.success('Authentication key revoked successfully');
      
      // Close dialog and redirect back to reader details
      handleCloseConfirmDialog();
      navigate(`/rfid/readers/${id}`);
    } catch (error) {
      console.error('Error revoking key:', error);
      toast.error('Failed to revoke authentication key');
      handleCloseConfirmDialog();
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reader) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Reader not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/rfid/readers"
          sx={{ mt: 2 }}
        >
          Back to Readers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={`/rfid/readers/${id}`}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Revoke Authentication Key
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Reader Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Device ID
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.device_id}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Key Created At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.auth_key_created_at 
                  ? new Date(reader.auth_key_created_at).toLocaleString() 
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Key Expires At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reader.auth_key_expires_at 
                  ? new Date(reader.auth_key_expires_at).toLocaleString() 
                  : 'Never'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              Warning: Revoking the authentication key will prevent the reader from authenticating with the system.
            </Typography>
            <Typography variant="body2">
              You will need to generate a new key for the reader to function again.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Revocation Reason"
                value={reason}
                onChange={handleReasonChange}
                multiline
                rows={3}
                helperText="Optional: Provide a reason for revoking this key"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/rfid/readers/${id}`}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={handleOpenConfirmDialog}
                >
                  Revoke Key
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>Confirm Key Revocation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke the authentication key for reader "{reader.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={revoking}>
            Cancel
          </Button>
          <Button 
            onClick={handleRevokeKey} 
            color="error" 
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={20} /> : <BlockIcon />}
          >
            {revoking ? 'Revoking...' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KeyRevocation;